import { createRootRoute, Link, Outlet, useRouterState } from "@tanstack/react-router";
import { Flame } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Layer 1: Global Navigation Shell
 *
 * Persistent across ALL pages:
 * - Brand / logo (top-left)
 * - Top navigation bar (desktop)
 * - Mobile bottom navigation
 *
 * Does NOT contain page-specific chrome (titles, action bars, breadcrumbs).
 * That's Layer 2 (page shell), owned by each route.
 */

export const Route = createRootRoute({
  component: Root,
});

const NAV_ITEMS = [
  { to: "/", label: "Home", icon: "Home" as const },
  { to: "/feed", label: "Feed", icon: "MessageSquare" as const },
  { to: "/posts", label: "Post", icon: "Plus" as const },
  { to: "/accounts", label: "Accounts", icon: "Users" as const },
];

function Root() {
  const { location } = useRouterState();
  const path = location.pathname;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      {/* ─── Desktop Top Nav ─── */}
      <header className="border-b bg-card/80 backdrop-blur-md sticky top-0 z-50 hidden sm:flex h-14 shrink-0">
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between px-6">
          {/* Brand */}
          <Link to="/" className="flex items-center gap-2 text-lg font-bold">
            <Flame className="h-5 w-5 text-orange-500" />
            HypeSocial
          </Link>

          {/* Global nav links */}
          <nav className="flex items-center gap-1">
            {NAV_ITEMS.map((item) => {
              const isActive = path === item.to || (item.to !== "/" && path.startsWith(item.to));
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={cn(
                    "flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
                    isActive
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted",
                  )}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>
      </header>

      {/* ─── Page Shell Layer (Layer 2) ─── */}
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>

      {/* ─── Mobile Bottom Nav ─── */}
      <nav className="fixed bottom-0 left-0 right-0 border-t bg-card/90 backdrop-blur-md z-50 sm:hidden h-14">
        <div className="flex items-center justify-around h-full">
          {NAV_ITEMS.map((item) => {
            const isActive = path === item.to || (item.to !== "/" && path.startsWith(item.to));
            return (
              <Link
                key={item.to}
                to={item.to}
                className={cn(
                  "flex flex-col items-center gap-0.5 py-1 px-3 rounded-lg transition-colors",
                  isActive ? "text-primary" : "text-muted-foreground",
                )}
              >
                <span className="text-[10px] font-medium">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Mobile bottom nav spacer */}
      <div className="h-14 sm:hidden" />
    </div>
  );
}
